import { Link, useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, ClipboardCheck, Info, LogOut, Receipt } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NAV, ROLE_LABELS } from "@/lib/i18n";

export default function MeirPage() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const { data: counts } = useQuery({ queryKey: ["counts"], queryFn: async () => (await api.get("/orders/counts")).data, enabled: isAdmin });
  const groups = [
    ...(isAdmin
      ? [
          {
            label: "Administrasjon",
            links: [
              { to: "/til-kontroll", label: NAV.tilKontroll, icon: ClipboardCheck, testId: "meir-til-kontroll", count: counts?.til_kontroll, hint: "Utførte ordrar som ventar på kontroll" },
              { to: "/fakturering", label: NAV.klarTilFakturering, icon: Receipt, testId: "meir-fakturering", count: counts?.klar_til_fakturering, hint: "Godkjende grunnlag, eksport og fakturert" },
              { to: "/register", label: NAV.register, icon: BookOpen, testId: "meir-register", hint: "Kundar, katalog, køyretøy og brukarar" },
            ],
          },
        ]
      : []),
    { label: "Hjelp", links: [{ to: "/om-systemet", label: NAV.omSystemet, icon: Info, testId: "meir-om-systemet", hint: "Korleis systemet heng saman" }] },
  ];
  return (
    <div className="space-y-5" data-testid="meir-page">
      <PageHeader title="Meir" />
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4" data-testid="meir-user-card">
        <UserAvatar name={user?.name} size="lg" solid testId="meir-user-avatar" />
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold">{user?.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</p>
        </div>
      </div>
      {groups.map((g) => (
        <section key={g.label} className="space-y-1.5">
          <p className="eyebrow px-1">{g.label}</p>
          <div className="divide-y divide-border rounded-lg border border-border bg-card">
            {g.links.map((l) => (
              <Link key={l.to} to={l.to} className="focus-ring flex min-h-[56px] items-center gap-3 px-4 py-2.5 first:rounded-t-lg last:rounded-b-lg hover:bg-muted active:bg-muted" data-testid={l.testId}>
                <l.icon className="h-[18px] w-[18px] shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{l.label}</span>
                  {l.hint ? <span className="block truncate text-xs text-muted-foreground">{l.hint}</span> : null}
                </span>
                {l.count ? <span className="tabular rounded-md bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground" data-testid={`${l.testId}-count`}>{l.count}</span> : null}
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </section>
      ))}
      <Button variant="outline" className="h-11 w-full bg-card md:h-10 md:w-auto" onClick={() => { logout(); navigate("/login"); }} data-testid="meir-logout-button">
        <LogOut className="mr-2 h-4 w-4" /> Logg ut
      </Button>
    </div>
  );
}
